import { supabase } from '../lib/supabase';
import { gameService, type Game, type GameFieldingEntry, type GameLineupEntry } from './gameService';
import { getGameFieldingInningsCount } from '../utils/gameFieldingInnings';
import type { Team } from '../types/core';

function getTodayDateStrLocal(): string {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

export function gameDateKey(iso: string): string {
  return iso.includes('T') ? iso.slice(0, 10) : iso.slice(0, 10);
}

/** Games on or after today (local), soonest first. */
export function listUpcomingGamesSorted(games: Game[]): Game[] {
  const today = getTodayDateStrLocal();
  return games
    .filter((g) => gameDateKey(g.date) >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
}

function opponentMatches(msg: string, opponent: string): boolean {
  const o = opponent.toLowerCase().trim();
  if (o.length < 2) return false;
  const m = msg.toLowerCase();
  return m.includes(o) || o.split(/\s+/).some((w) => w.length > 2 && m.includes(w));
}

/**
 * Best-effort: pick a game from natural language (upcoming-first).
 */
export function tryResolveGameFromMessage(text: string, games: Game[]): Game | null {
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();

  const uuid = trimmed.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
  );
  if (uuid) {
    const hit = games.find((g) => g.id.toLowerCase() === uuid[0].toLowerCase());
    if (hit) return hit;
  }

  const upcoming = listUpcomingGamesSorted(games);
  if (!upcoming.length) return null;

  if (/\bnext\s+game\b/.test(lower) || /\bthe\s+next\s+game\b/.test(lower)) {
    return upcoming[0] ?? null;
  }

  if (/\btoday'?s\s+game\b/.test(lower) || /\btonight'?s\s+game\b/.test(lower)) {
    const today = getTodayDateStrLocal();
    return upcoming.find((g) => gameDateKey(g.date) === today) ?? upcoming[0] ?? null;
  }

  const iso = trimmed.match(/\b20\d{2}-\d{2}-\d{2}\b/);
  if (iso) {
    const d = iso[0];
    const onDate = upcoming.filter((g) => gameDateKey(g.date) === d);
    if (onDate.length === 1) return onDate[0];
  }

  for (const g of upcoming) {
    if (opponentMatches(trimmed, g.opponent_name)) return g;
  }

  const against = trimmed.match(/(?:against|vs\.?)\s+(?:the\s+)?([A-Za-z0-9\s\-']+)/i);
  if (against?.[1]) {
    const frag = against[1].trim();
    const hit = upcoming.find((g) => g.opponent_name.toLowerCase().includes(frag.toLowerCase()));
    if (hit) return hit;
  }

  return null;
}

/** Remove P slots in [from,to] then add pitcher per inning (inclusive). */
export function mergePitcherInnings(
  existing: GameFieldingEntry[],
  blocks: Array<{ playerId: string; fromInning: number; toInning: number }>,
  pitcherKey = 'P'
): GameFieldingEntry[] {
  const capped = [...existing].filter((e) => {
    if (e.position !== pitcherKey) return true;
    return !blocks.some((b) => e.inning >= b.fromInning && e.inning <= b.toInning);
  });
  for (const b of blocks) {
    for (let inn = b.fromInning; inn <= b.toInning; inn++) {
      capped.push({ inning: inn, position: pitcherKey, player_id: b.playerId });
    }
  }
  return capped;
}

/** Assign one defensive slot for all innings 1..inningSpan (fills that position each inning like the UI diamond). */
export function mergePositionAllInnings(
  existing: GameFieldingEntry[],
  positionKey: string,
  playerId: string,
  maxInning: number
): GameFieldingEntry[] {
  const next = existing.filter((e) => !(e.position === positionKey && e.inning >= 1 && e.inning <= maxInning));
  for (let inn = 1; inn <= maxInning; inn++) {
    next.push({ inning: inn, position: positionKey, player_id: playerId });
  }
  return next;
}

export async function loadFieldingEntriesForCoachEdit(gameId: string, teamId: string): Promise<GameFieldingEntry[]> {
  let entries = await gameService.getGameFielding(gameId);
  if (entries.length > 0) return entries;
  const { data: depthChartData, error } = await supabase
    .from('depth_chart')
    .select('player_id, position, depth')
    .eq('team_id', teamId)
    .eq('unit', 'Fielding');
  if (error || !depthChartData) return [];
  return depthChartData.map((e: { player_id: string | null; position: string; depth: number }) => ({
    inning: e.depth,
    position: e.position,
    player_id: e.player_id ?? null
  }));
}

export async function persistGameFielding(
  gameId: string,
  entries: GameFieldingEntry[],
  team: Team
): Promise<void> {
  const maxInning = Math.max(getGameFieldingInningsCount(team), ...entries.map((e) => e.inning));
  const bounded = entries.filter((e) => e.inning >= 1 && e.inning <= maxInning && e.player_id);
  await gameService.setGameFielding(gameId, bounded);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('game-lineup-updated', { detail: { gameId } }));
  }
}

export async function persistManualBattingOrder(
  gameId: string,
  entries: GameLineupEntry[],
  currentSource: Game['lineup_source'] | undefined
): Promise<void> {
  await gameService.setGameLineup(gameId, entries);
  if (currentSource !== 'manual') {
    await gameService.updateGame(gameId, { lineup_source: 'manual' });
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('game-lineup-updated', { detail: { gameId } }));
  }
}
